import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Alert, CircularProgress, Grid,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AppService from '../../../../../Configurations/Appservice';

const initialFormState = {
  amount: '',
  paymentDate: new Date().toISOString().split('T')[0],
  taxPeriod: '',
  referenceNumber: '',
  notes: '',
  bankAccountId: '',
};

const RecordTaxPaymentDialog = ({ open, onClose, onSuccess, maxAmount = 0 }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBankAccountsLoading(true);
    AppService.get('/v1/admin/bank-accounts')
      .then((response) => {
        const all = Array.isArray(response) ? response : response?.data || [];
        setBankAccounts(all.filter((acc) => acc.status === 'ACTIVE'));
      })
      .catch(() => setBankAccounts([]))
      .finally(() => setBankAccountsLoading(false));
  }, [open]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
  };

  const handleSubmit = async () => {
    const amountNum = parseFloat(formData.amount);
    if (!formData.amount || amountNum <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    if (maxAmount > 0 && amountNum > maxAmount) {
      setError(`Amount cannot exceed the outstanding tax liability of AED ${Number(maxAmount).toFixed(2)}`);
      return;
    }
    if (!formData.paymentDate) {
      setError('Payment date is required');
      return;
    }
    if (!formData.bankAccountId) {
      setError('Bank account is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await AppService.post('v1/admin/finance/tax-payments', {
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        taxPeriod: formData.taxPeriod || null,
        referenceNumber: formData.referenceNumber || null,
        notes: formData.notes || null,
        bankAccountId: formData.bankAccountId,
      });
      setFormData(initialFormState);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record tax payment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Record Tax Payment</DialogTitle>
      <DialogContent>
        <Box mt={1}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Bank Account</InputLabel>
                <Select
                  value={formData.bankAccountId}
                  onChange={handleChange('bankAccountId')}
                  label="Bank Account"
                  disabled={bankAccountsLoading}
                >
                  {bankAccountsLoading && (
                    <MenuItem value="" disabled>Loading…</MenuItem>
                  )}
                  {!bankAccountsLoading && bankAccounts.length === 0 && (
                    <MenuItem value="" disabled>No active bank accounts</MenuItem>
                  )}
                  {bankAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.bankName} · {acc.accountNumber} · Balance: AED {Number(acc.balance || 0).toFixed(2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Amount (AED)"
                type="number"
                value={formData.amount}
                onChange={handleChange('amount')}
                fullWidth
                required
                inputProps={{ min: 0, max: maxAmount > 0 ? maxAmount : undefined, step: '0.01' }}
                helperText={
                  maxAmount > 0
                    ? `Outstanding liability: AED ${Number(maxAmount).toFixed(2)}`
                    : 'No outstanding tax liability'
                }
                error={
                  formData.amount !== '' &&
                  maxAmount > 0 &&
                  parseFloat(formData.amount) > maxAmount
                }
                disabled={maxAmount <= 0}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={handleChange('paymentDate')}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tax Period</InputLabel>
                <Select
                  value={formData.taxPeriod}
                  onChange={handleChange('taxPeriod')}
                  label="Tax Period"
                >
                  {(() => {
                    const year = new Date().getFullYear();
                    const periods = [];
                    for (let y = year; y >= year - 1; y--) {
                      periods.push(`Q1 ${y} (Jan-Mar)`);
                      periods.push(`Q2 ${y} (Apr-Jun)`);
                      periods.push(`Q3 ${y} (Jul-Sep)`);
                      periods.push(`Q4 ${y} (Oct-Dec)`);
                    }
                    return periods.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>);
                  })()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Reference Number"
                value={formData.referenceNumber}
                onChange={handleChange('referenceNumber')}
                fullWidth
                placeholder="Government receipt #"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                fullWidth
                multiline
                rows={2}
                placeholder="Optional description"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            loading ||
            maxAmount <= 0 ||
            (formData.amount !== '' &&
              maxAmount > 0 &&
              parseFloat(formData.amount) > maxAmount)
          }
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Recording...' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordTaxPaymentDialog;
